/**
 * Layout storage, replacing `%LOCALAPPDATA%\CitariaApp\layouts.json`.
 *
 * The WPF app is retired, so the constraint that froze this file — the agent
 * writing it in place so the desktop app could still open it, misspelled
 * `backgroun_path` and all — no longer applies. Layouts live in IndexedDB and
 * the background images live with them, which is what lets a layout survive
 * without an absolute path to a file the browser cannot reopen.
 *
 * The WIRE SHAPE is kept anyway, misspelling included, and that is deliberate:
 * it is what `importLegacy` reads and `exportAll` writes, so an operator's
 * existing layouts come across untouched and a backup taken from this console
 * is still recognisable. Every x/y/width/height remains in background-image
 * pixel space (§5) — nothing about the designer maths or the compositor
 * changes here.
 */

import type { LayoutConfig } from '@/core/types'
import { BLOBS, LAYOUTS, del, get, getAll, put } from './db'

/** A layout as stored: the wire shape plus the blobs that used to be paths. */
export interface StoredLayout extends LayoutConfig {
  /** Key into BLOBS for the background image. */
  background: string
  /** Key into BLOBS for the overlay drawn over the photos, or null. */
  overlay: string | null
  /** Key into BLOBS for `preview_<id>.png`, or null when generation failed. */
  preview: string | null
}

function backgroundKey(id: string): string {
  return `layout:${id}:background`
}

function overlayKey(id: string): string {
  return `layout:${id}:overlay`
}

function previewKey(id: string): string {
  return `layout:${id}:preview`
}

export async function listLayouts(): Promise<StoredLayout[]> {
  return getAll<StoredLayout>(LAYOUTS)
}

export async function getLayout(id: string): Promise<StoredLayout | undefined> {
  return get<StoredLayout>(LAYOUTS, id)
}

export async function layoutBackground(layout: StoredLayout): Promise<Blob | undefined> {
  return get<Blob>(BLOBS, layout.background)
}

export async function layoutOverlay(layout: StoredLayout): Promise<Blob | undefined> {
  return layout.overlay ? get<Blob>(BLOBS, layout.overlay) : undefined
}

export async function layoutPreview(layout: StoredLayout): Promise<Blob | undefined> {
  return layout.preview ? get<Blob>(BLOBS, layout.preview) : undefined
}

/** The images that go with a layout. Each is optional on an edit. */
export interface LayoutImages {
  /** Omitted on an edit: reopening a layout to move a slot must not require
   *  the operator to find the frame image again. */
  background?: Blob | null
  /** `null` REMOVES the overlay; `undefined` leaves whatever is stored. */
  overlay?: Blob | null
  preview?: Blob | null
}

/**
 * Writes a layout and its images.
 *
 * Named rather than positional because there are three blobs now and two of
 * them are optional — `saveLayout(l, bg, null, preview)` is exactly the kind of
 * call that silently wipes the wrong one.
 */
export async function saveLayout(
  layout: LayoutConfig,
  images: LayoutImages = {},
): Promise<StoredLayout> {
  const existing = await getLayout(layout.id)

  if (images.background) await put(BLOBS, images.background, backgroundKey(layout.id))
  if (images.preview) await put(BLOBS, images.preview, previewKey(layout.id))

  // Three states, and they are not the same: a new blob replaces, an explicit
  // null clears, and undefined keeps.
  let overlay = existing?.overlay ?? null
  if (images.overlay) {
    await put(BLOBS, images.overlay, overlayKey(layout.id))
    overlay = overlayKey(layout.id)
  } else if (images.overlay === null) {
    await del(BLOBS, overlayKey(layout.id)).catch(() => undefined)
    overlay = null
  }

  const stored: StoredLayout = {
    ...layout,
    background: backgroundKey(layout.id),
    overlay,
    preview: images.preview || existing?.preview ? previewKey(layout.id) : null,
  }

  await put(LAYOUTS, stored)
  return stored
}

export async function deleteLayout(id: string): Promise<void> {
  await del(BLOBS, backgroundKey(id)).catch(() => undefined)
  await del(BLOBS, overlayKey(id)).catch(() => undefined)
  await del(BLOBS, previewKey(id)).catch(() => undefined)
  await del(LAYOUTS, id)
}

// ── import and export ────────────────────────────────────────────────────────

/** The last path segment of a Windows or POSIX path. */
export function basename(path: string): string {
  const parts = path.replace(/\\/g, '/').split('/')
  return parts[parts.length - 1] ?? path
}

/**
 * Reads a `layouts.json` written by the desktop app or the agent.
 *
 * The JSON holds absolute paths (`C:\Users\…\frame.png`) that a browser cannot
 * open, so the operator also grants the folder those images sit in and each
 * `backgroun_path` is resolved BY FILE NAME within it. That is why the import
 * asks for a folder rather than the one file: a layouts.json usually references
 * several frames, and picking them one at a time in the right order is exactly
 * the kind of step that goes wrong the evening before an event.
 *
 * Layouts whose background cannot be found are reported rather than dropped
 * silently — an operator who imports six and gets four needs to know which two.
 */
export interface ImportResult {
  imported: StoredLayout[]
  /** Layout ids whose background image was not in the chosen folder. */
  missing: { id: string; background: string }[]
}

export async function importLegacy(
  json: string,
  images: FileSystemDirectoryHandle,
): Promise<ImportResult> {
  const parsed = JSON.parse(json) as LayoutConfig[]
  if (!Array.isArray(parsed)) throw new Error('layouts.json tidak berisi daftar layout')

  // One pass over the folder: `getFileHandle` is case-sensitive, and a
  // layouts.json written by the WPF app frequently disagrees with the file
  // system on case.
  const byName = new Map<string, FileSystemFileHandle>()
  for await (const entry of images.values()) {
    if (entry.kind === 'file') byName.set(entry.name.toLowerCase(), entry as FileSystemFileHandle)
  }

  const imported: StoredLayout[] = []
  const missing: { id: string; background: string }[] = []

  for (const layout of parsed) {
    const wanted = basename(layout.backgroun_path ?? '').toLowerCase()
    const handle = byName.get(wanted)
    if (!handle) {
      missing.push({ id: layout.id, background: layout.backgroun_path })
      continue
    }

    const background = await handle.getFile()

    // An overlay is resolved the same way, when the legacy file names one. The
    // desktop app never wrote this field, so it is normally absent.
    const overlayName = basename(layout.overlay_path ?? '').toLowerCase()
    const overlayHandle = overlayName ? byName.get(overlayName) : undefined

    // The preview is regenerated from the layout rather than imported: it is
    // derived data, and `preview_<id>.png` may well not be in the same folder.
    imported.push(
      await saveLayout(layout, {
        background,
        overlay: overlayHandle ? await overlayHandle.getFile() : null,
      }),
    )
  }

  return { imported, missing }
}

/**
 * A self-contained backup of every layout.
 *
 * Backgrounds are inlined as data URLs rather than zipped beside the JSON, so
 * the file restores with nothing but itself — the operator is copying this to a
 * USB stick the night before an event, not managing an archive.
 */
export interface LayoutBackup {
  version: 1
  /** `overlay_data` is absent on layouts without one, and on older backups. */
  layouts: (LayoutConfig & { background_data: string; overlay_data?: string })[]
}

function toDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('Tidak bisa membaca gambar'))
    reader.readAsDataURL(blob)
  })
}

export async function exportAll(): Promise<Blob> {
  const layouts = await listLayouts()
  const out: LayoutBackup = { version: 1, layouts: [] }

  for (const layout of layouts) {
    const background = await layoutBackground(layout)
    if (!background) continue

    const overlay = await layoutOverlay(layout)
    const { background: _b, overlay: _o, preview: _p, ...wire } = layout

    out.layouts.push({
      ...wire,
      background_data: await toDataUrl(background),
      ...(overlay ? { overlay_data: await toDataUrl(overlay) } : {}),
    })
  }

  return new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' })
}

export async function importBackup(json: string): Promise<StoredLayout[]> {
  const parsed = JSON.parse(json) as LayoutBackup
  if (parsed.version !== 1) throw new Error('Versi cadangan tidak dikenal')

  const imported: StoredLayout[] = []
  for (const entry of parsed.layouts) {
    const { background_data, overlay_data, ...layout } = entry
    imported.push(
      await saveLayout(layout, {
        background: await (await fetch(background_data)).blob(),
        overlay: overlay_data ? await (await fetch(overlay_data)).blob() : null,
      }),
    )
  }
  return imported
}
